import { useCallback, useRef, type MouseEvent } from 'react';

/** 스타일 `approvalHorizontalScrollbarStyles` 가 이 속성이 달린 영역에만 가로 스크롤바 막대를 칠한다 */
export const HOVER_SCROLLBAR_ATTR = 'data-hover-scrollbar';

const isHorizontalScroller = (el: HTMLElement) =>
  el.scrollWidth > el.clientWidth &&
  ['auto', 'scroll'].includes(getComputedStyle(el).overflowX);

/**
 * 마우스를 올린 가로 스크롤 영역에만 스크롤바 막대를 보여 준다.
 * Chrome 은 :hover 만으로는 막대를 다시 그리지 않아 속성을 직접 붙였다 뗀다.
 */
const useHoverScrollbar = () => {
  const currentRef = useRef<HTMLElement | null>(null);

  const mark = useCallback((el: HTMLElement | null) => {
    if (currentRef.current === el) return;
    currentRef.current?.removeAttribute(HOVER_SCROLLBAR_ATTR);
    el?.setAttribute(HOVER_SCROLLBAR_ATTR, '');
    currentRef.current = el;
  }, []);

  const onMouseOver = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      // 이미 표시한 영역 안에서 움직일 땐 다시 찾지 않음 — 조상마다 레이아웃을 읽어 무거움
      if (currentRef.current?.contains(e.target as Node)) return;
      let el = e.target as HTMLElement | null;
      while (el && el !== e.currentTarget && !isHorizontalScroller(el)) {
        el = el.parentElement;
      }
      mark(el === e.currentTarget ? null : el);
    },
    [mark],
  );

  const onMouseLeave = useCallback(() => mark(null), [mark]);

  return { onMouseOver, onMouseLeave };
};

export default useHoverScrollbar;
